import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/auth.service';
import { userService } from '../../services/user.service';
import { creatorService } from '../../services/creator.service';
import { requestCache } from '../../utils/requestCache';

// Types
export interface User {
  id: string;
  email: string;
  fullName?: string;
  brand?: string; // Keep for backward compatibility
  brand_name?: string; // New field from backend
  username?: string;
  phone?: string;
  role: string;
  accountType?: 'creator' | 'individual' | 'buyer';
  bio?: string;
  location?: string;
  profilePicture?: string;
  coverImage?: string;
  isVerified?: boolean;
  isActive?: boolean;
  profileComplete?: number;
  heardFrom?: string;
  termsAccepted?: boolean;
  emailVerified?: boolean;
  tempPassword?: boolean;
  socialLinks?: {
    instagram?: string;
    facebook?: string;
    website?: string;
    [key: string]: string | undefined;
  };
  specialty?: string[];
  createdAt?: string;
  updatedAt?: string;
  
  // Subscription fields
  subscriptionStatus?: 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial' | 'past_due' | null;
  subscriptionPlan?: string;
  subscriptionExpiresAt?: string;
  subscriptionNextBillingDate?: string;
  
  // New API fields (v1/users/me)
  isPremium?: boolean;
  planType?: 'free' | 'premium' | 'pro' | string;
  /**
   * Backend-driven feature flag map. Keys are sparse — only flags the server
   * has an opinion on are present. Includes both:
   *   - Subscription/capability flags (analytics, messaging, …)
   *   - Universal/experimental flags (wishlist_sync, buyer_signup, …)
   * The index signature lets new server-side flags land without an FE deploy.
   * Read via `useFeatureFlag(name)` rather than indexing this map directly so
   * default values stay centralized.
   */
  features?: {
    analytics?: boolean;
    messaging?: boolean;
    priorityListing?: boolean;
    featured?: boolean;
    verificationBadge?: boolean;
    /** Buyer wishlist server sync (FAVORITES_SERVER_SYNC). */
    wishlist_sync?: boolean;
    /** Show "Buyer" option on signup. */
    buyer_signup?: boolean;
    [key: string]: boolean | undefined;
  };
  
  // Legacy fields for backward compatibility
  verified?: boolean;
  designer_id?: string;
  subscription_tier?: string;
  is_verified?: boolean;
  portfolio_count?: number;
  last_login?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Helper functions for localStorage
const saveTokensToStorage = (token: string, refreshToken: string) => {
  localStorage.setItem('accessToken', token);
  localStorage.setItem('refreshToken', refreshToken);
};

const removeTokensFromStorage = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

const getTokensFromStorage = () => {
  const token = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  return { token, refreshToken };
};

// Initial state - restore tokens from localStorage but don't trust them until verified
const { token: storedToken, refreshToken: storedRefreshToken } = getTokensFromStorage();
const initialState: AuthState = {
  user: null,
  token: storedToken,
  refreshToken: storedRefreshToken,
  isAuthenticated: false, // Don't trust tokens until verified via API
  isLoading: !!storedToken, // Show loading state if we need to verify tokens
  error: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const signup = createAsyncThunk(
  'auth/signup',
  async (
    userData: {
      fullName: string;
      email: string;
      phone: string;
      password: string;
      heardFrom?: string;
      termsAccepted: boolean;
      accountType?: 'creator' | 'individual' | 'buyer';
    },
    { rejectWithValue },
  ) => {
    try {
      // console.log(userData, "response from signup")

      const response = await authService.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Signup failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const response = await authService.refreshToken(state.auth.refreshToken!);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // Don't cache user verification - always fetch fresh to ensure token validity
      const response = await userService.getCurrentUser();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (updateData: any, { rejectWithValue }) => {
    try {
      const response = await userService.updateProfile(updateData);
      // Clear the fetchCurrentUser cache so next fetch gets fresh data
      requestCache.delete('fetchCurrentUser');
      // Invalidate cached creator profiles (current user may be cached under
      // both UUID and username — clear them all to be safe).
      creatorService.invalidateCreator();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

export const updateProfilePicture = createAsyncThunk(
  'auth/updateProfilePicture',
  async (imageFile: File, { rejectWithValue }) => {
    try {
      const response = await userService.updateProfilePicture(imageFile);
      // Clear the fetchCurrentUser cache so next fetch gets fresh data
      requestCache.delete('fetchCurrentUser');
      // Invalidate cached creator profiles so public views refresh too.
      creatorService.invalidateCreator();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile picture');
    }
  }
);

export const updateCoverImage = createAsyncThunk(
  'auth/updateCoverImage',
  async (imageFile: File, { rejectWithValue }) => {
    try {
      const response = await userService.updateCoverImage(imageFile);
      // Clear the fetchCurrentUser cache so next fetch gets fresh data
      requestCache.delete('fetchCurrentUser');
      // Invalidate cached creator profiles so public views refresh too.
      creatorService.invalidateCreator();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cover image');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send reset email');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refreshToken;
      state.isAuthenticated = true;
      // Persist tokens to localStorage
      saveTokensToStorage(token, refreshToken);
    },
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      // Remove tokens from localStorage
      removeTokensFromStorage();
      // Clear request cache to prevent stale data
      requestCache.clear();
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.data) {
          state.user = action.payload.data.user;
          state.token = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken;
          state.isAuthenticated = true;
          // Persist tokens to localStorage
          saveTokensToStorage(action.payload.data.accessToken, action.payload.data.refreshToken);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Signup
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.data) {
          // Only auto-login if session exists (email confirmation not required)
          if (action.payload.data.session && !action.payload.data.requiresEmailConfirmation) {
            state.user = action.payload.data.user;
            state.token = action.payload.data.session.access_token;
            state.refreshToken = action.payload.data.session.refresh_token;
            state.isAuthenticated = true;
            // Persist tokens to localStorage
            saveTokensToStorage(action.payload.data.session.access_token, action.payload.data.session.refresh_token);
          }
          // If requiresEmailConfirmation is true, user stays logged out until they confirm
        }
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Refresh Token
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (action.payload.success && action.payload.data) {
          state.token = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken;
          // Persist updated tokens to localStorage
          saveTokensToStorage(action.payload.data.accessToken, action.payload.data.refreshToken);
        }
      })
      .addCase(refreshToken.rejected, (state) => {
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        // Remove tokens from localStorage on refresh failure
        removeTokensFromStorage();
        // Clear request cache to prevent stale data
        requestCache.clear();
      })
      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && action.payload.data) {
          state.user = action.payload.data;
          state.isAuthenticated = true; // Only set authenticated after successful verification
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // Clear authentication state if fetching user fails (invalid/expired tokens)
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        // Remove tokens from localStorage
        removeTokensFromStorage();
        // Clear request cache to prevent stale data
        requestCache.clear();
      })
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && 'data' in action.payload && action.payload.data) {
          state.user = { 
            ...state.user, 
            ...action.payload.data,
            id: state.user?.id || action.payload.data.id,
            email: state.user?.email || action.payload.data.email,
            phone: action.payload.data.phone || state.user?.phone,
          };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Profile Picture
      .addCase(updateProfilePicture.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && 'data' in action.payload && action.payload.data && state.user) {
          state.user = { 
            ...state.user, 
            profilePicture: action.payload.data.profilePicture,
          };
        }
      })
      .addCase(updateProfilePicture.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Cover Image
      .addCase(updateCoverImage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCoverImage.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.success && 'data' in action.payload && action.payload.data && state.user) {
          state.user = { 
            ...state.user, 
            coverImage: action.payload.data.coverImage,
          };
        }
      })
      .addCase(updateCoverImage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCredentials, clearCredentials, setError, clearError } = authSlice.actions;
export default authSlice.reducer; 